import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SERVICE_URLS } from "./api/authApiClient";

// Standalone, auth-less BI widget for iframe embedding. Reads its opaque token from the URL,
// fetches the aggregate-only public payload, and renders a compact portfolio summary. No app
// chrome, no login — designed to live inside a client-facing portal.
const C = { ink: "#10254A", slate: "#33415C", muted: "#6A778E", line: "#E6EBF3", blue: "#2F6BE0", green: "#3FA13B", amber: "#E7A13C", red: "#E24B4A" };
const fmtUSD = (n) => (!n ? "$0" : n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(0)}K` : `$${Math.round(n).toLocaleString()}`);
const bandColor = (s) => (s == null ? C.muted : s <= 3.5 ? C.green : s <= 6.5 ? C.amber : C.red);

const Stat = ({ label, value, accent }) => (
  <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: "12px 14px", minWidth: 0 }}>
    <div style={{ fontSize: 10.5, letterSpacing: ".06em", textTransform: "uppercase", color: C.muted, fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: accent || C.ink, lineHeight: 1.15 }}>{value}</div>
  </div>
);

const EmbedWidget = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const base = SERVICE_URLS.underwriting;
        const res = await fetch(`${base}/public/embed/${token}`);
        if (!res.ok) throw new Error("bad");
        const json = await res.json();
        if (alive) setData(json?.data || null);
      } catch (e) { if (alive) setErr(true); }
    })();
    return () => { alive = false; };
  }, [token]);

  const wrap = { fontFamily: "'Segoe UI',-apple-system,Helvetica,Arial,sans-serif", padding: 16, background: "#F5F7FB", minHeight: "100vh", boxSizing: "border-box" };

  if (err) return <div style={{ ...wrap, color: C.muted, fontSize: 14 }}>This embedded report is unavailable or its link has expired.</div>;
  if (!data) return <div style={{ ...wrap, color: C.muted, fontSize: 14 }}>Loading…</div>;

  const s = data.summary || {};
  return (
    <div style={wrap}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <svg width="20" height="20" viewBox="0 0 26 26" aria-hidden="true"><path d="M20.5 7.2A9 9 0 1 0 22 12" fill="none" stroke="#10254A" strokeWidth="2.6" strokeLinecap="round" /><circle cx="13" cy="13" r="3.1" fill="#10254A" /></svg>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>CrediArc — Portfolio summary</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
        <Stat label="Counterparties" value={(s.counterparties || 0).toLocaleString()} />
        <Stat label="Recommended" value={fmtUSD(s.totalRecommendedExposure)} />
        <Stat label="Approved" value={fmtUSD(s.totalApprovedExposure)} accent={C.green} />
        <Stat label="Avg ARC" value={s.avgScore != null ? s.avgScore.toFixed(1) : "—"} accent={bandColor(s.avgScore)} />
        <Stat label="High-risk %" value={`${s.highRiskPct || 0}%`} accent={C.red} />
        <Stat label="Pending" value={(s.pending || 0).toLocaleString()} accent={C.amber} />
      </div>
      {Array.isArray(s.topSectors) && s.topSectors.length > 0 && (
        <div style={{ marginTop: 14, background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: C.muted, marginBottom: 8 }}>Top sectors by exposure</div>
          {s.topSectors.map((t, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderTop: i ? `1px solid ${C.line}` : "none", fontSize: 13, color: C.slate }}>
              <span>{t.name}</span><span style={{ fontWeight: 700, color: C.ink }}>{fmtUSD(t.exposure)}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 10.5, color: C.muted }}>Updated {new Date(data.generatedAt).toLocaleString()}</div>
    </div>
  );
};

export default EmbedWidget;
